/* ==========================================================================
   Buena Vita — routine builder

   Selection state lives in localStorage keyed by product handle, so a routine
   picked on the homepage is still there on a product page. Prices are read
   from the tiles rather than stored, so a price change in admin can never be
   served stale out of someone's browser storage.

   Depends on window.BVFX (bv-fx.js) for money formatting, the atomic multi-add,
   and the toast. It degrades rather than throws if that file failed to load:
   the tiles still toggle and the total still adds up, only the cart call is
   unavailable — and the button says so instead of silently doing nothing.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bvStackInit) return;
  window.__bvStackInit = true;

  var KEY = 'bv:routine';

  function $all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function readSel() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (err) {
      return [];
    }
  }

  function writeSel(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (err) { /* private mode */ }
  }

  /* BVFX may not have executed yet when this runs; look it up per call rather
     than caching a reference at load time. */
  function money(cents) {
    if (window.BVFX && window.BVFX.money) return window.BVFX.money(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  function threshold() {
    return window.BVFX && window.BVFX.threshold ? window.BVFX.threshold() : 0;
  }

  var X_SVG =
    '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
    '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  function initSection(section) {
    if (section.dataset.bvStackBound === '1') return;
    section.dataset.bvStackBound = '1';

    var tiles = $all('[data-bv-tile]', section);
    if (!tiles.length) return;

    var chips = $all('[data-bv-stack-filter]', section);
    var list = section.querySelector('[data-bv-sum-list]');
    var empty = section.querySelector('[data-bv-sum-empty]');
    var totalBox = section.querySelector('[data-bv-sum-total]');
    var totalVal = section.querySelector('[data-bv-sum-value]');
    var shipBox = section.querySelector('[data-bv-sum-ship]');
    var shipText = section.querySelector('[data-bv-sum-ship-text]');
    var shipFill = section.querySelector('[data-bv-sum-ship-fill]');
    var go = section.querySelector('[data-bv-sum-go]');
    var goLabel = go ? go.textContent.trim() : 'Add routine to cart';

    var selected = readSel();

    /* On a product page the product being viewed joins the routine on arrival —
       that is what makes this read as "what goes with this one". */
    var pre = section.getAttribute('data-bv-preselect');
    if (pre && selected.indexOf(pre) === -1 && tiles.some(function (t) {
      return t.getAttribute('data-bv-handle') === pre;
    })) {
      selected.push(pre);
      writeSel(selected);
    }

    /* Drop handles that are no longer on offer — a product can be unpublished
       between visits, and a phantom line in the panel would be unexplainable. */
    var available = tiles.map(function (t) { return t.getAttribute('data-bv-handle'); });
    var pruned = selected.filter(function (h) { return available.indexOf(h) !== -1; });
    if (pruned.length !== selected.length) { selected = pruned; writeSel(selected); }

    function tileFor(handle) {
      return tiles.filter(function (t) { return t.getAttribute('data-bv-handle') === handle; })[0];
    }

    function paint() {
      tiles.forEach(function (t) {
        t.setAttribute('aria-pressed', selected.indexOf(t.getAttribute('data-bv-handle')) !== -1 ? 'true' : 'false');
      });

      var total = 0;
      var rows = '';

      selected.forEach(function (handle) {
        var t = tileFor(handle);
        if (!t) return;
        var price = parseInt(t.getAttribute('data-bv-price'), 10) || 0;
        total += price;
        rows +=
          '<li class="bv-sum__row">' +
          '<span>' + t.getAttribute('data-bv-title') + '</span>' +
          '<b>' + money(price) + '</b>' +
          '<button type="button" class="bv-sum__drop" data-bv-drop="' + handle + '" ' +
          'aria-label="Remove ' + t.getAttribute('data-bv-title') + '">' + X_SVG + '</button>' +
          '</li>';
      });

      var any = selected.length > 0;

      if (list) { list.innerHTML = rows; list.hidden = !any; }
      if (empty) empty.hidden = any;
      if (totalBox) totalBox.hidden = !any;
      if (totalVal) totalVal.textContent = money(total);

      if (go) {
        go.disabled = !any;
        go.textContent = any
          ? goLabel + ' · ' + selected.length + (selected.length === 1 ? ' item' : ' items')
          : goLabel;
      }

      var limit = threshold();
      if (shipBox && limit > 0) {
        shipBox.hidden = !any;
        if (any) {
          var left = limit - total;
          if (left <= 0) {
            shipText.innerHTML = '<strong>Free shipping included.</strong>';
            shipFill.style.width = '100%';
          } else {
            shipText.innerHTML = '<strong>' + money(left) + '</strong> away from free shipping';
            shipFill.style.width = Math.max(4, Math.round((total / limit) * 100)) + '%';
          }
        }
      }
    }

    function toggle(handle) {
      var i = selected.indexOf(handle);
      if (i === -1) selected.push(handle);
      else selected.splice(i, 1);
      writeSel(selected);
      paint();
    }

    tiles.forEach(function (t) {
      t.addEventListener('click', function () { toggle(t.getAttribute('data-bv-handle')); });
    });

    if (list) {
      list.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-bv-drop]') : null;
        if (!btn) return;
        toggle(btn.getAttribute('data-bv-drop'));
      });
    }

    /* ---------- goal chips ---------- */

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var want = chip.getAttribute('data-bv-stack-filter');
        var current = chips.filter(function (c) { return c.getAttribute('aria-pressed') === 'true'; })[0];
        var currentVal = current ? current.getAttribute('data-bv-stack-filter') : '*';
        var next = currentVal === want && want !== '*' ? '*' : want;

        chips.forEach(function (c) {
          c.setAttribute('aria-pressed', c.getAttribute('data-bv-stack-filter') === next ? 'true' : 'false');
        });

        tiles.forEach(function (t) {
          var li = t.parentElement;
          var keep = next === '*' || (t.getAttribute('data-bv-tags') || '').split(/\s+/).indexOf(next) !== -1;
          if (li) li.hidden = !keep;
        });
      });
    });

    /* ---------- add the whole routine ---------- */

    if (go) {
      go.addEventListener('click', function () {
        if (!selected.length) return;

        if (!window.BVFX || !window.BVFX.addItems) {
          go.textContent = 'Cart unavailable — please refresh';
          return;
        }

        var items = selected
          .map(function (h) {
            var t = tileFor(h);
            return t ? { id: parseInt(t.getAttribute('data-bv-variant'), 10), quantity: 1 } : null;
          })
          .filter(Boolean);

        go.disabled = true;
        go.textContent = 'Adding…';

        window.BVFX.addItems(items).then(function (res) {
          if (res.ok) {
            window.BVFX.toast({
              title: 'Routine added to your cart',
              sub: selected.length + (selected.length === 1 ? ' item' : ' items'),
              showMeter: true
            });
            /* The routine is now IN the cart, so the builder starts clean —
               leaving it selected invites a shopper to add the same stack a
               second time without noticing. */
            selected = [];
            writeSel(selected);
          } else {
            window.BVFX.toast({ title: 'Could not add that routine', sub: res.message, error: true });
          }
          go.disabled = false;
          paint();
        });
      });
    }

    /* The goal finder writes into the same localStorage key and then fires this
       event. Without it the builder would keep showing the selection it read at
       load time, and a shopper who just finished the quiz would scroll down to
       an empty panel. Re-read rather than trusting the payload, so localStorage
       stays the single source of truth. */
    document.addEventListener('bv:routine:changed', function () {
      selected = readSel().filter(function (h) { return available.indexOf(h) !== -1; });
      paint();
    });

    paint();
  }

  function boot(scope) {
    $all('[data-bv-stack]', scope || document).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
